import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { loadMe } from '@app/store/actions/profile.actions';
import { ContextService } from '@core/services';
import { BillingV2Service } from '@core/services/lingo2-account/billing-v2.service';
import {
  BillingApiContractorDto,
  BillingApiReportCombinedResponseDto,
  BillingApiReportContractorQueryDto,
} from '@lingo2-billing-sdk/models';
import { BillingApiReportCombinedTypeEnum } from '@lingo2-billing-sdk/models/src/billing-api/billing-api-report/types/enums/billing-api-report-combined-type.enum';
import { PaginationDto } from '@lingo2-billing-sdk/models/src/shared/types/dtos/pagination.dto';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { getMe } from '@store/reducers/profile.reducer';
import {
  AccountBalanceTypeEnum,
  BillingContractorDescriptorEnum,
  IBillingCard,
  IContentBlock,
  User,
} from 'lingo2-models';
import { IPagination } from 'lingo2-models/dist/interfaces';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface HistoryTab {
  id: number;
  code: string;
  title: string;
}

@Component({
  selector: 'app-finance-history-page',
  templateUrl: './finance-history-page.component.html',
  styleUrls: ['./finance-history-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinanceHistoryPageComponent extends ChangableComponent implements OnInit {
  private billingV2Service = inject(BillingV2Service);
  private contextService = inject(ContextService);
  protected fb = inject(UntypedFormBuilder);
  private readonly store = inject(Store);
  deviceService = inject(DeviceDetectorService);

  @Input() schoolId: string;

  public me: User;

  public isFocus: boolean;
  public form: UntypedFormGroup;
  public tabs: HistoryTab[] = [
    {
      id: 1,
      code: 'all',
      title: 'transaction-history.tabs.all',
    },
    {
      id: 2,
      code: 'buy',
      title: 'transaction-history.tabs.purchases',
    },
    {
      id: 3,
      code: 'sell',
      title: 'transaction-history.tabs.sales',
    },
    {
      id: 4,
      code: 'payout',
      title: 'transaction-history.tabs.payouts',
    },
  ];

  public selectedTab: HistoryTab = this.tabs[0];

  public pagination: IPagination = {
    page: 1,
    pageSize: 25,
  };

  public transactions: Partial<IContentBlock<BillingApiReportCombinedResponseDto, BillingApiReportContractorQueryDto>> =
    {
      loading: false,
      loaded: false,
      items: [],
      filter: { type: [] },
    };
  private cards: IBillingCard[];

  private currentTransactionRequest: Subscription;

  public ngOnInit() {
    this.form = this.fb.group({
      search_term: [''],
      from: [],
      to: [],
    });

    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.me = me;
          this.contextService.updateUser(me);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        },
      });

    this.goTab(this.tabs[0], true);
  }

  public onFocus() {
    this.isFocus = !this.isFocus;
  }

  public clearSearchForm() {
    this.form.get('search_term').setValue('');
  }

  public onChangedStartDate(e: Date) {
    this.form.get('from').setValue(new Date(e));
    this.pagination.page = 1;
    this.loadTransactions();
  }

  public onChangedEndDate(e: Date) {
    this.form.get('to').setValue(new Date(e));
    this.pagination.page = 1;
    this.loadTransactions();
  }

  public resetDateFilter(period: string) {
    this.form.get(period).setValue(null);
    this.pagination.page = 1;
    this.loadTransactions();
  }

  public loadTransactions() {
    this.transactions.loaded = false;

    const from = this.form.get('from').value;
    const to = this.form.get('to').value;
    const request = new BillingApiReportContractorQueryDto();
    const requestPagination = new PaginationDto();

    request.type = this.transactions.filter.type;
    request.providerId = this.transactions.filter.providerId;
    request.from = from;
    request.to = to;

    // Фильтрация по мета будет добавлена на сервере позже
    // request.chargeMeta = { tenant: this.features.tenant };

    requestPagination.page = this.pagination.page;
    requestPagination.pageSize = this.pagination.pageSize;

    const contractor = new BillingApiContractorDto();

    if (this.schoolId) {
      contractor.descriptor = BillingContractorDescriptorEnum.school;
      contractor.externalId = this.schoolId;
    }

    if (this.currentTransactionRequest && !this.currentTransactionRequest.closed) {
      this.currentTransactionRequest.unsubscribe();
    }

    this.currentTransactionRequest = this.billingV2Service
      .findTransactions(request, requestPagination, contractor)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this.transactions.items = res.results;
          this.transactions.loaded = true;
          this.pagination = res.pagination;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public goTab(tab: HistoryTab, force?: boolean) {
    if (tab.code === this.selectedTab.code && !force) {
      return;
    }
    this.selectedTab = tab;

    switch (tab.code) {
      case 'buy':
        this.transactions.filter.type = [
          BillingApiReportCombinedTypeEnum.BUY,
          BillingApiReportCombinedTypeEnum.BUY_REFUND,
        ];
        break;
      case 'sell':
        this.transactions.filter.type = [
          BillingApiReportCombinedTypeEnum.SELL,
          BillingApiReportCombinedTypeEnum.SELL_REFUND,
        ];
        break;
      case 'payout':
        this.transactions.filter.type = [BillingApiReportCombinedTypeEnum.PAYOUT];
        break;
      case 'all':
        this.transactions.filter.type = [];
        break;
    }
    this.pagination.page = 1;
    this.loadTransactions();
  }

  public loadPageTransaction(page: number) {
    this.pagination.page = page;
    this.loadTransactions();
  }

  public changeProvider(provider: string) {
    this.transactions.filter.providerId = provider;
    this.loadTransactions();
  }

  public getCardName(card_id: string) {
    const card = this.cards.find((c) => c.id === card_id);
    return card?.title ? card?.title : '';
  }

  public getBalanceName(balance: AccountBalanceTypeEnum): string {
    if (!balance) {
      return '';
    }
    const type = AccountBalanceTypeEnum[balance];
    if (!type) {
      return '';
    }
    return `user-profile.billing.balance_type.${type}`;
  }
}
